/**
 * Aceite de convite (E9, MT-906). Espelha `authentication.forms.InvitationAcceptForm` e a
 * `InvitationAcceptSerializer`: nome, CPF, telefone opcional e a senha que o convidado escolhe.
 *
 * O e-mail não é campo — o token já o prova. O CPF é validado de verdade no backend
 * (`validated_cpf`, decisão 15 de E9); aqui só conferimos o tamanho, como no resto do app.
 */
import { z } from 'zod';

import type { InvitationAcceptRequest } from '~/api/invitations';
import { digitsOnly, isValidCpfCnpjLength } from '~/lib/masks';

export const MINIMUM_PASSWORD_LENGTH = 8;

export const invitationAcceptSchema = z
  .object({
    full_name: z.string().trim().min(1, 'Informe o seu nome completo'),
    cpf: z
      .string()
      .trim()
      .refine((value) => isValidCpfCnpjLength(value), 'CPF inválido'),
    phone: z.string().trim(),
    password: z
      .string()
      .min(
        MINIMUM_PASSWORD_LENGTH,
        `A senha precisa de pelo menos ${MINIMUM_PASSWORD_LENGTH} caracteres`,
      ),
    password_confirm: z.string(),
  })
  .refine((form) => form.password === form.password_confirm, {
    message: 'As senhas não conferem',
    path: ['password_confirm'],
  });

export type InvitationForm = z.infer<typeof invitationAcceptSchema>;

export const INVITATION_FIELDS = [
  'full_name',
  'cpf',
  'phone',
  'password',
  'password_confirm',
] as const;

export type InvitationField = (typeof INVITATION_FIELDS)[number];
export type InvitationFieldErrors = Partial<Record<InvitationField, string>>;

export const EMPTY_INVITATION_FORM: InvitationForm = {
  full_name: '',
  cpf: '',
  phone: '',
  password: '',
  password_confirm: '',
};

/** O que uma conta que já existe envia: nada. O clique no link é o aceite (decisão 4 de E9). */
export const EMPTY_ACCEPT_PAYLOAD: InvitationAcceptRequest = {
  full_name: '',
  cpf: '',
  phone: '',
  password: '',
};

export interface CheckedInvitationForm {
  /** O corpo do `POST`, ou `null` quando algum campo está errado. */
  payload: InvitationAcceptRequest | null;
  errors: InvitationFieldErrors;
}

function firstMessageByField(error: z.ZodError<InvitationForm>): InvitationFieldErrors {
  const { fieldErrors } = error.flatten();
  const errors: InvitationFieldErrors = {};
  for (const field of INVITATION_FIELDS) {
    const [message] = fieldErrors[field] ?? [];
    if (message) {
      errors[field] = message;
    }
  }
  return errors;
}

/** Máscara na tela, dígitos puros no corpo; a senha vai como foi digitada. */
function acceptPayloadOf(form: InvitationForm): InvitationAcceptRequest {
  return {
    full_name: form.full_name,
    cpf: digitsOnly(form.cpf),
    phone: digitsOnly(form.phone),
    password: form.password,
  };
}

export function checkInvitationAccept(form: InvitationForm): CheckedInvitationForm {
  const result = invitationAcceptSchema.safeParse(form);
  if (!result.success) {
    return { payload: null, errors: firstMessageByField(result.error) };
  }
  return { payload: acceptPayloadOf(result.data), errors: {} };
}

/** Os erros por campo do backend (`INVITATION_DATA_INVALID`), só nos campos que a tela mostra. */
export function invitationFieldErrors(byField: Record<string, string>): InvitationFieldErrors {
  const errors: InvitationFieldErrors = {};
  for (const field of INVITATION_FIELDS) {
    const message = byField[field];
    if (message) {
      errors[field] = message;
    }
  }
  return errors;
}
